import type { EventHookOn } from '@vueuse/core';
import { getCurrentScope, nextTick, onScopeDispose, type Ref } from 'vue';
import { getSerpHotelPositionsData, hasPersonalProfile, tracker, TrackingEvent } from '@/entities/analytics';
import type { ProductSearch, ProductSearchEvents } from '@/entities/product';
import type { SearchCriteria } from '@/entities/searchCriteria';
import { createUuid } from '@/shared/lib/createUuid';
import type { UseLowPriceCalendar } from '@/widgets/lowPriceCalendar';
import type { FiltersModel, FiltersModelChange, SerpProduct, SerpSort } from './types';
import { SERP_TRACKING_PRODUCT_TYPES, SERP_TRACKING_SORT_TYPES } from '../config/tracking';
import {
  getCalendarTrackingData,
  getFirstHotelsPrices,
  getSerpSearchFiltersData,
  getSerpSearchResultsData,
  getSerpStartEmptySearchData,
  getSerpStartSearchData,
  hasSearchCountry,
  isGroupSearchSerpProduct,
  isTrackedFiltersChange,
  toSerpHotelPositions,
  type SerpSearchFiltersTrackingOptions,
} from '../lib/serpTracking';

interface SerpTrackingFilters {
  filtersModel: Readonly<Ref<FiltersModel>>;
  filteredProducts: Readonly<Ref<SerpProduct[]>>;
  onFiltersModelChange: EventHookOn<FiltersModelChange>;
}

interface SerpTrackingOptions {
  userSearchUuid: Readonly<Ref<string | null>>;
  calendar: Pick<UseLowPriceCalendar, 'onLoad'>;
  products: Readonly<Ref<SerpProduct[]>>;
  filters: SerpTrackingFilters;
}

interface SerpTrackingHotelList {
  products: readonly SerpProduct[];
  sort: SerpSort;
}

interface SerpTracking {
  trackHotelOpen(serpProduct: SerpProduct, list: SerpTrackingHotelList): void;
}

interface TrackedUserSearch {
  searchUuid: string;
  criteria: SearchCriteria;
  firstHotelsPrices: Map<number, number>;
}

type FiltersTrackingSnapshot = Omit<SerpSearchFiltersTrackingOptions, 'renderTime' | 'meta'>;

interface DeferredFiltersChange {
  previous: FiltersModel;
  replaced: boolean;
}

const DISABLED_SERP_TRACKING: SerpTracking = {
  trackHotelOpen(): void {
    return undefined;
  },
};

function trackFilters(snapshot: FiltersTrackingSnapshot, renderTime: number | null): void {
  tracker.track(TrackingEvent.SearchFilters, Date.now(), (meta) =>
    getSerpSearchFiltersData({ ...snapshot, renderTime, meta }),
  );
}

export function useSerpTracking(
  productSearch: ProductSearch,
  { userSearchUuid, calendar, products, filters }: SerpTrackingOptions,
): SerpTracking {
  const productType = SERP_TRACKING_PRODUCT_TYPES[productSearch.type];

  if (!productType) return DISABLED_SERP_TRACKING;

  let userSearch: TrackedUserSearch | null = null;
  let trackedUserSearchUuid: string | null = null;
  let filtersSearchUuid: string | null = null;
  let lastFiltersUuid: string | null = null;
  let deferredFiltersChange: DeferredFiltersChange | null = null;

  const getFiltersSnapshot = (searchUuid: string, autoChange: boolean): FiltersTrackingSnapshot => {
    const uuid = createUuid();
    lastFiltersUuid = uuid;

    return {
      searchUuid,
      uuid,
      autoChange,
      filtersModel: filters.filtersModel.value,
      products: products.value,
      filteredProducts: filters.filteredProducts.value,
    };
  };

  const trackEmptySearchStart = ({ searchUuid, criteria }: ProductSearchEvents['start']): void => {
    const originalSearchUuid = userSearchUuid.value;

    if (!originalSearchUuid || originalSearchUuid !== trackedUserSearchUuid || !hasSearchCountry(criteria)) return;

    filtersSearchUuid = searchUuid;
    const timestamp = Date.now();
    const personalProfile = hasPersonalProfile();

    queueMicrotask(() => {
      tracker.track(TrackingEvent.EmptySearchStart, timestamp, (meta) =>
        getSerpStartEmptySearchData({ productType, originalSearchUuid, searchUuid, criteria, personalProfile, meta }),
      );
    });
  };

  const onStart = (start: ProductSearchEvents['start']): void => {
    const { searchUuid, criteria } = start;

    if (searchUuid !== userSearchUuid.value) {
      trackEmptySearchStart(start);
      return;
    }

    if (!hasSearchCountry(criteria)) {
      userSearch = null;
      trackedUserSearchUuid = null;
      filtersSearchUuid = null;
      return;
    }

    userSearch = { searchUuid, criteria, firstHotelsPrices: new Map() };
    trackedUserSearchUuid = searchUuid;
    filtersSearchUuid = searchUuid;
    const personalProfile = hasPersonalProfile();

    tracker.track(TrackingEvent.SearchStart, Date.now(), (meta) =>
      getSerpStartSearchData({ productType, searchUuid, criteria, personalProfile, meta }),
    );
    if (!deferredFiltersChange) return;

    const { previous, replaced } = deferredFiltersChange;
    deferredFiltersChange = null;
    if (isTrackedFiltersChange(previous, filters.filtersModel.value)) {
      trackFilters(getFiltersSnapshot(searchUuid, replaced), null);
    }
  };

  const onSyncEnd = (snapshot: ProductSearchEvents['syncEnd']): void => {
    if (!userSearch || userSearch.searchUuid !== productSearch.searchUuid) return;

    userSearch.firstHotelsPrices = getFirstHotelsPrices(snapshot);
  };

  const onEnd = (result: ProductSearchEvents['end']): void => {
    if (!userSearch || userSearch.searchUuid !== result.searchUuid) return;

    const { criteria, firstHotelsPrices } = userSearch;
    userSearch = null;

    if (result.state !== 'finished') return;

    const personalProfile = hasPersonalProfile();

    tracker.track(TrackingEvent.SearchResults, result.timings.startedAt ?? Date.now(), (meta) =>
      getSerpSearchResultsData({ productType, result, criteria, firstHotelsPrices, personalProfile, meta }),
    );
  };

  const onFiltersModelChange = ({ previous, filtersModel, replaced }: FiltersModelChange): void => {
    if (!isTrackedFiltersChange(previous, filtersModel)) return;

    if (!filtersSearchUuid) {
      deferredFiltersChange = { previous: deferredFiltersChange?.previous ?? previous, replaced };
      return;
    }

    const snapshot = getFiltersSnapshot(filtersSearchUuid, replaced);
    const changedAt = performance.now();
    const runSearchUuid = productSearch.searchUuid;

    nextTick(() => {
      const isListRendered = productSearch.searchUuid === runSearchUuid;

      trackFilters(snapshot, isListRendered ? Math.round(performance.now() - changedAt) : null);
    });
  };

  const trackHotelOpen = (serpProduct: SerpProduct, list: SerpTrackingHotelList): void => {
    if (isGroupSearchSerpProduct(serpProduct) || !list.products.includes(serpProduct)) return;

    const data = getSerpHotelPositionsData({
      searchUuid: productSearch.searchUuid,
      filteredSearchUuid: lastFiltersUuid,
      sortType: SERP_TRACKING_SORT_TYPES[list.sort],
      hotels: toSerpHotelPositions(list.products),
      selectedHotelId: serpProduct.hotel.id,
    });

    tracker.track(TrackingEvent.SerpHotelPositions, Date.now(), () => data);
  };

  const unsubscribes = [
    productSearch.on('start', onStart),
    productSearch.on('syncEnd', onSyncEnd),
    productSearch.on('end', onEnd),
  ];

  filters.onFiltersModelChange(onFiltersModelChange);

  calendar.onLoad((load) => {
    if (!load.days.length) return;

    tracker.track(TrackingEvent.Calendar, load.startedAt, (meta) => getCalendarTrackingData(load, meta));
  });

  if (getCurrentScope()) {
    onScopeDispose(() => unsubscribes.forEach((unsubscribe) => unsubscribe()));
  }

  return { trackHotelOpen };
}
